/**
 * Shared tax-calendar period presets used by the receipts table and the
 * export dialog. Keeping both surfaces on one definition prevents the labels
 * and computed date windows from drifting apart.
 *
 * The fiscal year comes from the client's country, not a hardcoded Australian
 * one: AU starts 1 Jul, GB starts 6 Apr, the US and anything unlisted use the
 * calendar year. The backend derives the same windows from the same country
 * config (`api/constants/countries.py` → `get_fiscal_year_bounds`), so an
 * export and the table it was launched from cover identical dates.
 */

export type PeriodPreset =
  | "all"
  | "this_fy"
  | "last_fy"
  | "this_q"
  | "last_q"
  | "custom";

/** The country-driven shape of a fiscal year. Mirrors the API's `country_config`. */
export interface FiscalYear {
  /** ISO 3166-1 alpha-2, e.g. "AU". Drives label wording only. */
  code: string;
  /** 1–12 */
  startMonth: number;
  /** 1–31 */
  startDay: number;
}

/** Fallback for callers that have not loaded a country config yet. */
export const DEFAULT_FISCAL_YEAR: FiscalYear = {
  code: "AU",
  startMonth: 7,
  startDay: 1,
};

/** Build a FiscalYear from the API's `country_config` payload. */
export function fiscalYearFromConfig(
  config:
    | {
        code?: string | null;
        fiscal_year_start_month?: number | null;
        fiscal_year_start_day?: number | null;
      }
    | null
    | undefined,
): FiscalYear {
  if (
    !config ||
    !config.fiscal_year_start_month ||
    !config.fiscal_year_start_day
  ) {
    return DEFAULT_FISCAL_YEAR;
  }
  return {
    code: config.code ?? "",
    startMonth: config.fiscal_year_start_month,
    startDay: config.fiscal_year_start_day,
  };
}

export interface ResolvedPeriod {
  /** ISO date string `YYYY-MM-DD` or null for "no lower bound". */
  from: string | null;
  /** ISO date string `YYYY-MM-DD` or null for "no upper bound". */
  to: string | null;
  /** Short trigger label, e.g. "This FY (FY26)" or "Q4 (Apr-Jun 2026)". */
  label: string;
  /** Human date range caption, e.g. "1 Jul 2025 to 30 Jun 2026". */
  caption: string;
}

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** Local-date ISO string. `toISOString()` would shift by the UTC offset. */
function toIso(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function fmt(isoDate: string): string {
  const [y, m, d] = isoDate.split("-").map(Number);
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

/** Shift by whole months, clamping the day into the target month. */
function addMonths(date: Date, months: number): Date {
  const target = new Date(
    date.getFullYear(),
    date.getMonth() + months,
    1,
  );
  const lastDay = new Date(
    target.getFullYear(),
    target.getMonth() + 1,
    0,
  ).getDate();
  target.setDate(Math.min(date.getDate(), lastDay));
  return target;
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

/** Start of the fiscal year containing `today`. */
export function fiscalYearStart(today: Date, fy: FiscalYear): Date {
  const thisYear = new Date(today.getFullYear(), fy.startMonth - 1, fy.startDay);
  return today >= thisYear
    ? thisYear
    : new Date(today.getFullYear() - 1, fy.startMonth - 1, fy.startDay);
}

/** Inclusive [from, to] of the fiscal year starting at `start`. */
function fyBounds(start: Date): [string, string] {
  return [toIso(start), toIso(addDays(addMonths(start, 12), -1))];
}

type Quarter = 0 | 1 | 2 | 3;

/** Inclusive [from, to] of quarter `q` (0-based) of the year starting at `start`. */
function quarterBounds(start: Date, q: Quarter): [string, string] {
  const qStart = addMonths(start, 3 * q);
  return [toIso(qStart), toIso(addDays(addMonths(qStart, 3), -1))];
}

/** Which quarter of its fiscal year `today` falls in. */
function currentQuarter(today: Date, start: Date): Quarter {
  const months =
    (today.getFullYear() - start.getFullYear()) * 12 +
    (today.getMonth() - start.getMonth()) -
    (today.getDate() < start.getDate() ? 1 : 0);
  return Math.min(3, Math.max(0, Math.floor(months / 3))) as Quarter;
}

/**
 * What a fiscal year is called locally. AU and NZ say "FY26"; the UK and most
 * other non-calendar countries say "2025/26"; calendar-year countries just say
 * the year.
 */
export function fiscalYearLabel(start: Date, fy: FiscalYear): string {
  const year = start.getFullYear();
  if (fy.startMonth === 1 && fy.startDay === 1) return String(year);
  if (fy.code === "AU" || fy.code === "NZ") {
    return `FY${String(year + 1).slice(-2)}`;
  }
  return `${year}/${String(year + 1).slice(-2)}`;
}

/** "FY" reads as Australian; everywhere else says "tax year". */
export function fiscalYearNoun(fy: FiscalYear): string {
  return fy.code === "AU" || fy.code === "NZ" ? "FY" : "tax year";
}

function quarterLabel(q: Quarter): string {
  return (["Q1", "Q2", "Q3", "Q4"] as const)[q];
}

/** Month span of a quarter, e.g. "Jul-Sep", derived from its real bounds. */
function quarterRangeLabel([from, to]: [string, string]): string {
  const fromMonth = MONTHS[Number(from.split("-")[1]) - 1];
  const toMonth = MONTHS[Number(to.split("-")[1]) - 1];
  return `${fromMonth}-${toMonth}`;
}

/**
 * Resolve a period preset into an explicit date window + display labels.
 * For "custom" the caller supplies the date inputs; this helper fills in
 * the caption from whatever dates are passed.
 */
export function resolvePeriod(
  preset: PeriodPreset,
  opts: {
    today?: Date;
    customFrom?: string;
    customTo?: string;
    fiscalYear?: FiscalYear;
  } = {},
): ResolvedPeriod {
  const today = opts.today ?? new Date();
  const fy = opts.fiscalYear ?? DEFAULT_FISCAL_YEAR;

  if (preset === "all") {
    return { from: null, to: null, label: "All time", caption: "" };
  }

  if (preset === "custom") {
    const from = opts.customFrom || null;
    const to = opts.customTo || null;
    const caption = from && to ? `${fmt(from)} to ${fmt(to)}` : "Pick a date range";
    return { from, to, label: "Custom range", caption };
  }

  const start = fiscalYearStart(today, fy);
  const noun = fiscalYearNoun(fy);

  if (preset === "this_fy" || preset === "last_fy") {
    const yearStart = preset === "this_fy" ? start : addMonths(start, -12);
    const [from, to] = fyBounds(yearStart);
    const which = preset === "this_fy" ? "This" : "Last";
    return {
      from,
      to,
      label: `${which} ${noun} (${fiscalYearLabel(yearStart, fy)})`,
      caption: `${fmt(from)} to ${fmt(to)}`,
    };
  }

  if (preset === "this_q") {
    const q = currentQuarter(today, start);
    const bounds = quarterBounds(start, q);
    return {
      from: bounds[0],
      to: bounds[1],
      label: `This quarter (${quarterLabel(q)} ${quarterRangeLabel(bounds)})`,
      caption: `${fmt(bounds[0])} to ${fmt(bounds[1])}`,
    };
  }

  // last_q: step back one quarter, wrapping into the previous year if needed.
  const q = currentQuarter(today, start);
  const prev = (q === 0 ? 3 : q - 1) as Quarter;
  const prevStart = prev > q ? addMonths(start, -12) : start;
  const bounds = quarterBounds(prevStart, prev);
  return {
    from: bounds[0],
    to: bounds[1],
    label: `Last quarter (${quarterLabel(prev)} ${quarterRangeLabel(bounds)})`,
    caption: `${fmt(bounds[0])} to ${fmt(bounds[1])}`,
  };
}

/** Option list for a <Select> dropdown. Labels are computed fresh so they
 *  always carry the current FY / quarter numbers. */
export function periodOptions(
  today: Date = new Date(),
  fiscalYear: FiscalYear = DEFAULT_FISCAL_YEAR,
): Array<{
  value: PeriodPreset;
  label: string;
  caption: string;
}> {
  return (["all", "this_fy", "last_fy", "this_q", "last_q", "custom"] as const).map(
    (p) => {
      const r = resolvePeriod(p, { today, fiscalYear });
      return { value: p, label: r.label, caption: r.caption };
    },
  );
}
